import React, { useState, useEffect } from 'react';
import { 
  Wand2, Target, TrendingUp, AlertCircle, Calendar, DollarSign, Users,
  Brain, Zap, CheckCircle2, Clock, BarChart3, Sparkles, Play, Settings2, Loader2
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Campaign {
  id: string;
  campaign_name: string;
  campaign_type: string;
  objective: string;
  target_audience: string | null;
  content: any;
  status: string;
  target_count: number;
  sent_count: number;
  opened_count: number;
  clicked_count: number;
  converted_count: number;
  open_rate: number;
  click_rate: number;
  conversion_rate: number;
  created_at: string;
}

interface Insight {
  id: string;
  insight_type: string;
  insight_title: string;
  insight_description: string;
  impact_level: string;
  is_active: boolean;
}

export default function CampaignGeneratorPage() {
  const [activeTab, setActiveTab] = useState<'generated' | 'insights' | 'performance'>('generated');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: campaignsData, error: campaignsError } = await supabase
        .from('marketing_generated_campaigns')
        .select('*')
        .eq('company_id', 'halesac')
        .order('created_at', { ascending: false });

      if (campaignsError) throw campaignsError;

      const { data: insightsData, error: insightsError } = await supabase
        .from('marketing_campaign_insights')
        .select('*')
        .eq('company_id', 'halesac')
        .eq('is_active', true)
        .order('created_at', { ascending: false});

      if (insightsError) throw insightsError;

      setCampaigns(campaignsData || []);
      setInsights(insightsData || []);
    } catch (err: any) {
      console.error('Error loading campaign data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateCampaignStatus = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('marketing_generated_campaigns')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
      await loadData();
    } catch (err: any) {
      console.error('Error updating campaign:', err);
      alert('Failed to update campaign: ' + err.message);
    }
  };

  const calculateMetrics = () => {
    const total = campaigns.length;
    const active = campaigns.filter(c => c.status === 'active').length;
    const completed = campaigns.filter(c => c.status === 'completed').length;
    const avgOpenRate = total > 0 
      ? (campaigns.reduce((sum, c) => sum + (c.open_rate || 0), 0) / total).toFixed(1)
      : '0';
    const totalRevenue = campaigns.reduce((sum, c) => sum + (c.converted_count * 250), 0);

    return [
      { label: 'Total Campaigns', value: total.toString(), change: `${active} active` },
      { label: 'Completed Campaigns', value: completed.toString(), change: `${((completed/total)*100 || 0).toFixed(0)}% of total` },
      { label: 'Average Open Rate', value: `${avgOpenRate}%`, change: 'Across all campaigns' },
      { label: 'Total Conversions', value: campaigns.reduce((sum, c) => sum + c.converted_count, 0).toString(), change: `$${totalRevenue.toLocaleString()} revenue` }
    ];
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-700',
      scheduled: 'bg-blue-100 text-blue-700',
      draft: 'bg-gray-100 text-gray-700',
      completed: 'bg-purple-100 text-purple-700',
      paused: 'bg-yellow-100 text-yellow-700',
      archived: 'bg-gray-100 text-gray-500'
    };
    return colors[status] || colors.draft;
  };

  const getImpactColor = (level: string) => {
    const colors: Record<string, string> = {
      high: 'border-red-300 bg-red-50',
      medium: 'border-yellow-300 bg-yellow-50',
      low: 'border-blue-300 bg-blue-50'
    };
    return colors[level] || colors.medium;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading campaign data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 max-w-2xl mx-auto">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 mb-2">Failed to Load Campaign Data</h3>
              <p className="text-red-700 mb-4">{error}</p>
              <button
                onClick={loadData}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
              <Wand2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Campaign Generator</h1>
              <p className="text-gray-600 mt-1">AI-powered campaign management and insights</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Brain className="w-6 h-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-2">Campaign System Status</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm text-gray-600">{campaigns.length} Campaigns</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                <span className="text-sm text-gray-600">{campaigns.filter(c => c.status === 'active').length} Active</span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-gray-600">{insights.length} Insights</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span className="text-sm text-gray-600">{campaigns.filter(c => c.status === 'completed').length} Completed</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('generated')}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'generated' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500'
            }`}
          >
            Generated Campaigns ({campaigns.length})
          </button>
          <button
            onClick={() => setActiveTab('insights')}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'insights' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500'
            }`}
          >
            AI Insights ({insights.length})
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'performance' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500'
            }`}
          >
            Performance
          </button>
        </div>
      </div>

      {activeTab === 'generated' && (
        <>
          {campaigns.length === 0 ? (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-12 text-center">
              <Wand2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Campaigns Yet</h3>
              <p className="text-gray-600 mb-4">Start generating AI-powered campaigns based on market intelligence.</p>
              <button 
                onClick={() => alert('Campaign generation is coming soon! This page currently displays AI-generated campaigns from the marketing intelligence system.')}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Generate First Campaign
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-purple-300 transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{campaign.campaign_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                          {campaign.status.toUpperCase()}
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {campaign.campaign_type}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{campaign.objective}</p>
                      {campaign.target_audience && (
                        <p className="text-sm text-gray-500 mt-1">Target: {campaign.target_audience}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={campaign.status}
                        onChange={(e) => updateCampaignStatus(campaign.id, e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="draft">Draft</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                        <option value="completed">Completed</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-6 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-xs font-medium text-gray-500 uppercase">Audience</span>
                      </div>
                      <p className="text-lg font-semibold text-gray-900">{campaign.target_count.toLocaleString()}</p>
                      <p className="text-xs text-gray-600">{campaign.sent_count} sent</p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <BarChart3 className="w-4 h-4 text-gray-400" />
                        <span className="text-xs font-medium text-gray-500 uppercase">Open Rate</span>
                      </div>
                      <p className="text-lg font-semibold text-gray-900">{campaign.open_rate?.toFixed(1) || 0}%</p>
                      <p className="text-xs text-gray-600">{campaign.opened_count} opens</p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Target className="w-4 h-4 text-gray-400" />
                        <span className="text-xs font-medium text-gray-500 uppercase">Click Rate</span>
                      </div>
                      <p className="text-lg font-semibold text-gray-900">{campaign.click_rate?.toFixed(1) || 0}%</p>
                      <p className="text-xs text-gray-600">{campaign.clicked_count} clicks</p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        <span className="text-xs font-medium text-gray-500 uppercase">Conversions</span>
                      </div>
                      <p className="text-lg font-semibold text-gray-900">{campaign.converted_count}</p>
                      <p className="text-xs text-gray-600">{campaign.conversion_rate?.toFixed(1) || 0}% rate</p>
                    </div>
                  </div>

                  {campaign.content?.subject && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700">{campaign.content.subject}</p>
                      {campaign.content.preview_text && (
                        <p className="text-sm text-gray-600 mt-1">{campaign.content.preview_text}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'insights' && (
        <div className="space-y-4">
          {insights.length === 0 ? (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-12 text-center">
              <Sparkles className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Insights Available</h3>
              <p className="text-gray-600">AI insights will appear here as campaigns are analyzed.</p>
            </div>
          ) : (
            insights.map((insight) => (
              <div key={insight.id} className={`rounded-xl border-2 p-6 ${getImpactColor(insight.impact_level)}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <h3 className="text-lg font-semibold text-gray-900">{insight.insight_title}</h3>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-white">
                    {insight.impact_level.toUpperCase()} IMPACT
                  </span>
                </div>
                <p className="text-gray-700">{insight.insight_description}</p>
                <div className="mt-3 flex gap-2">
                  <span className="px-2 py-1 bg-white rounded text-xs font-medium text-gray-600">
                    {insight.insight_type.replace('_', ' ')}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'performance' && (
        <div className="grid grid-cols-2 gap-6">
          {calculateMetrics().map((metric, index) => (
            <div key={index} className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <p className="text-sm font-medium text-gray-600 mb-2">{metric.label}</p>
              <p className="text-3xl font-bold text-gray-900 mb-1">{metric.value}</p>
              <p className="text-sm text-gray-600">{metric.change}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
